import { collection, doc, getDoc, getDocs, limit, orderBy, query, where } from 'firebase/firestore';
import { db } from '@/core/services/firebase';
import type { Claim } from '@/core/types';

const DISPLAY_COUNT = 30;

/** ランダムな開始位置から完走証を取得 */
export async function getRandomCerts(raceAlias: string, excludeId?: string): Promise<Claim[]> {
  const claimsRef = collection(db, 'claims');
  const startPoint = Math.random();

  // randomSeed >= startPoint で取得を試みる
  const q1 = query(
    claimsRef,
    where('raceAlias', '==', raceAlias),
    where('deletedAt', '==', null),
    where('randomSeed', '>=', startPoint),
    orderBy('randomSeed', 'asc'),
    limit(DISPLAY_COUNT + 1) // excludeId分を考慮して+1
  );

  const snapshot1 = await getDocs(q1);
  let docs = snapshot1.docs.filter((doc) => {
    const data = doc.data();
    return data.fileName != null;
  });

  // 足りない場合はラップアラウンド（randomSeed < startPoint から取得）
  // excludeId分を考慮して+1件必要
  if (docs.length < DISPLAY_COUNT + 1) {
    const remaining = DISPLAY_COUNT - docs.length + 1;
    const q2 = query(
      claimsRef,
      where('raceAlias', '==', raceAlias),
      where('deletedAt', '==', null),
      where('randomSeed', '<', startPoint),
      orderBy('randomSeed', 'asc'),
      limit(remaining)
    );

    const snapshot2 = await getDocs(q2);
    const additionalDocs = snapshot2.docs.filter((doc) => {
      const data = doc.data();
      return data.fileName != null;
    });

    docs = [...docs, ...additionalDocs];
  }

  // excludeIdを除外して30件に
  const certs = docs
    .filter((doc) => doc.id !== excludeId)
    .slice(0, DISPLAY_COUNT)
    .map((doc) => ({
      id: doc.id,
      ...doc.data(),
    })) as Claim[];

  return certs;
}

/** ゼッケン番号から完走証を取得 */
export async function getCertByBib(raceAlias: string, bib: string): Promise<Claim | null> {
  const runnerId = `${raceAlias}-${bib}`;
  const claimRef = doc(db, 'claims', runnerId);
  const claimSnap = await getDoc(claimRef);

  if (!claimSnap.exists()) {
    return null;
  }

  const data = claimSnap.data();
  if (data.deletedAt != null || data.fileName == null) {
    return null;
  }

  return {
    id: claimSnap.id,
    ...data,
  } as Claim;
}

/** ユーザーの完走証を取得（認証済みユーザー用） */
export async function getUserCert(raceAlias: string, uid: string): Promise<Claim | null> {
  const claimsRef = collection(db, 'claims');
  const q = query(
    claimsRef,
    where('raceAlias', '==', raceAlias),
    where('uid', '==', uid),
    where('deletedAt', '==', null),
    limit(1)
  );

  const snapshot = await getDocs(q);

  if (snapshot.empty) {
    return null;
  }

  const docData = snapshot.docs[0];
  const data = docData.data();

  // fileNameがない（動画生成未完了）場合はnull
  if (data.fileName == null) {
    return null;
  }

  return {
    id: docData.id,
    ...data,
  } as Claim;
}
